import React from 'react';
import { FiPieChart, FiUsers, FiDollarSign, FiClock, FiPlusCircle } from 'react-icons/fi';

export default function Dashboard() {
  // Mock data - replace with real API calls later
  const userStats = {
    balance: 185000,
    activeGroups: 3,
    nextPayout: 'June 25, 2023',
    totalSaved: 420000
  };

  const savingsGroups = [
    { id: 1, name: 'Family Ajo', progress: 75, target: 200000, members: 8 },
    { id: 2, name: 'Tech Bros Circle', progress: 30, target: 500000, members: 12 },
    { id: 3, name: 'Market Women', progress: 90, target: 300000, members: 10 }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Welcome back, <span className="text-purple-600">Oluwaseun</span></h1>
          <p className="text-gray-500">Track your savings and groups</p>
        </div>
        <button className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-5 py-3 rounded-lg transition-colors">
          <FiPlusCircle size={18} />
          New Group
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard 
          icon={<FiDollarSign className="text-green-500" size={24} />} 
          title="Wallet Balance" 
          value={`₦${userStats.balance.toLocaleString()}`} 
        />
        <StatCard 
          icon={<FiUsers className="text-blue-500" size={24} />} 
          title="Active Groups" 
          value={userStats.activeGroups} 
        />
        <StatCard 
          icon={<FiClock className="text-yellow-500" size={24} />} 
          title="Next Payout" 
          value={userStats.nextPayout} 
        />
        <StatCard 
          icon={<FiPieChart className="text-purple-500" size={24} />} 
          title="Total Saved" 
          value={`₦${userStats.totalSaved.toLocaleString()}`} 
        />
      </div>

      {/* Savings Groups Section */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Your Savings Groups</h2>
          <button className="text-purple-600 hover:text-purple-800 text-sm font-medium">
            View All
          </button>
        </div>

        <div className="space-y-6">
          {savingsGroups.map(group => (
            <GroupCard key={group.id} group={group} />
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Recent Activity</h2>
        <div className="space-y-4">
          <ActivityItem 
            type="contribution" 
            group="Family Ajo" 
            amount={50000} 
            date="2 hours ago" 
          />
          <ActivityItem 
            type="payout" 
            group="Market Women" 
            amount={300000} 
            date="1 day ago" 
          />
          <ActivityItem 
            type="joined" 
            group="Tech Bros Circle" 
            date="3 days ago" 
          />
        </div>
      </div>
    </div>
  );
}

// Reusable Components
function StatCard({ icon, title, value }) {
  return (
    <div className="bg-white p-6 rounded-xl shadow-sm flex items-start gap-4">
      <div className="p-3 bg-gray-100 rounded-lg">{icon}</div>
      <div>
        <p className="text-gray-500 text-sm">{title}</p>
        <p className="text-xl font-semibold text-gray-800">{value}</p>
      </div>
    </div>
  );
}

function GroupCard({ group }) {
  return (
    <div className="border border-gray-100 rounded-lg p-5 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <h3 className="font-medium text-lg text-gray-800">{group.name}</h3>
        <span className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
          {group.members} members
        </span>
      </div>
      
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-500 mb-1">
          <span>Progress</span>
          <span>{group.progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-purple-600 h-2 rounded-full" 
            style={{ width: `${group.progress}%` }}
          ></div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <span className="text-sm text-gray-500">Target: ₦{group.target.toLocaleString()}</span>
        <button className="text-purple-600 hover:text-purple-800 text-sm font-medium">
          View Details
        </button>
      </div>
    </div>
  );
}

function ActivityItem({ type, group, amount, date }) {
  const icons = {
    contribution: <FiDollarSign className="text-green-500" />,
    payout: <FiDollarSign className="text-blue-500" />,
    joined: <FiUsers className="text-purple-500" />
  };

  const descriptions = {
    contribution: `Contributed ₦${amount?.toLocaleString()} to ${group}`,
    payout: `Received ₦${amount?.toLocaleString()} from ${group}`,
    joined: `Joined ${group} savings circle`
  };

  return (
    <div className="flex items-center gap-4">
      <div className="p-2 bg-gray-100 rounded-lg">
        {icons[type]}
      </div>
      <div className="flex-1">
        <p className="text-gray-800">{descriptions[type]}</p>
        <p className="text-sm text-gray-500">{date}</p>
      </div>
    </div>
  );
}